import { OidcClient } from "@axa-fr/react-oidc";
import type { AccessTokenProvider, IdentityScope, TokenRequestReason } from "./access-token-provider";
import { clearCurrentSessionCache, createSessionCacheScope } from "./session-cache";
import { getSessionExpirationState, notifySessionExpired } from "./session-expiration";

/**
 * 独立工作台的 Token Provider：沿用 `@axa-fr/react-oidc` 的登录、续期与退出，
 * 身份作用域仍从 ID Token claims 推导，行为与抽象前一致（设计 §9.2）。
 */
class StandaloneOidcAccessTokenProvider implements AccessTokenProvider {
  private listeners = new Set<() => void>();
  private subscriptionId: string | null = null;
  private attachTimer: number | undefined;
  private cachedIdToken: string | null = null;
  private cachedScope: IdentityScope = null;

  async getAccessToken(): Promise<string | null> {
    if (this.isSessionExpired()) {
      return null;
    }
    return OidcClient.get()?.tokens?.accessToken ?? null;
  }

  async requestAccessToken(_reason: TokenRequestReason): Promise<string | null> {
    if (this.isSessionExpired()) {
      return null;
    }
    const oidc = OidcClient.get();
    if (!oidc) {
      throw new Error("OIDC 客户端尚未初始化，无法刷新 token。");
    }
    await oidc.renewTokensAsync();
    return oidc.tokens?.accessToken ?? null;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    this.attachOidcEvents();
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this.detachOidcEvents();
      }
    };
  }

  getIdentityScope(): IdentityScope {
    const tokens = OidcClient.get()?.tokens;
    const idToken = tokens?.idToken ?? null;
    // 同一个 ID Token 必须返回同一个对象引用，否则 useSyncExternalStore 会无限重渲染。
    if (idToken === this.cachedIdToken) {
      return this.cachedScope;
    }
    this.cachedIdToken = idToken;
    this.cachedScope = createSessionCacheScope(tokens?.idTokenPayload);
    return this.cachedScope;
  }

  isSessionExpired(): boolean {
    return getSessionExpirationState() !== "active";
  }

  async onAuthenticationFailed(_reason: TokenRequestReason): Promise<void> {
    clearCurrentSessionCache();
    notifySessionExpired();
  }

  clear(): void {
    this.cachedIdToken = null;
    this.cachedScope = null;
    this.notify();
  }

  private notify() {
    this.listeners.forEach((listener) => {
      listener();
    });
  }

  /**
   * `OidcClient` 由 `OidcProvider` 创建，首个订阅者可能早于它就位；
   * 拿不到实例时按短间隔重试，直到最后一个订阅者退订。
   */
  private attachOidcEvents() {
    if (this.subscriptionId !== null || this.attachTimer !== undefined) {
      return;
    }

    const oidc = OidcClient.get();
    if (!oidc) {
      this.attachTimer = window.setTimeout(() => {
        this.attachTimer = undefined;
        if (this.listeners.size > 0) {
          this.attachOidcEvents();
        }
      }, 200);
      return;
    }

    this.subscriptionId = oidc.subscribeEvents((name) => {
      if (TOKEN_EVENT_NAMES.has(name)) {
        this.notify();
      }
    });
  }

  private detachOidcEvents() {
    if (this.attachTimer !== undefined) {
      window.clearTimeout(this.attachTimer);
      this.attachTimer = undefined;
    }
    if (this.subscriptionId !== null) {
      OidcClient.get()?.removeEventSubscription(this.subscriptionId);
      this.subscriptionId = null;
    }
  }
}

const TOKEN_EVENT_NAMES = new Set<string>([
  OidcClient.eventNames.token_acquired,
  OidcClient.eventNames.token_renewed,
  OidcClient.eventNames.logout_from_same_tab,
  OidcClient.eventNames.logout_from_another_tab,
]);

export const standaloneOidcAccessTokenProvider: AccessTokenProvider = new StandaloneOidcAccessTokenProvider();
